// --- imports you likely already have:
import {
    getFirestore, collection, query, orderBy, limit as fslimit, getDocs
  } from 'firebase/firestore';
  import { getAuth } from 'firebase/auth';
  
  // Your gateway base:
  const GATEWAY_BASE = 'https://llfconnect-gw-35q0po5z.uc.gateway.dev';
  
  // ID token (force refresh to ensure it’s valid for the gateway)
  async function getFreshIdToken() {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Not signed in');
    return user.getIdToken(/* forceRefresh */ true);
  }
  
  async function postJSON(url, body) {
    const idToken = await getFreshIdToken();
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Gateway ${resp.status}: ${text}`);
    }
    return resp.json();
  }
  
  // Unwrap a DEK for a single message
  async function unwrapDek(ownerUid, wrappedDEK) {
    const url = `${GATEWAY_BASE}/unwrapDek`;
    const { dekBase64 } = await postJSON(url, { uid: ownerUid, wrappedDEK });
    if (!dekBase64) throw new Error('unwrapDek: missing dekBase64');
    return dekBase64;
  }
  
  // --- base64 helpers (standard base64, not url-safe)
  function b64ToBytes(b64) {
    const bin = atob(b64);
    const len = bin.length;
    const out = new Uint8Array(len);
    for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  function concatUint8(a, b) {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0); out.set(b, a.length);
    return out;
  }
  
  // Decrypt one AES-GCM envelope using WebCrypto
  async function aesGcmDecrypt({ dekB64, nonceB64, ctB64, tagB64 }) {
    // WebCrypto expects ciphertext || tag in one buffer
    const rawKey = b64ToBytes(dekB64);
    const iv = b64ToBytes(nonceB64);
    const ct = b64ToBytes(ctB64);
    const tag = b64ToBytes(tagB64);
    const ctWithTag = concatUint8(ct, tag);
  
    const key = await crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-GCM' },
      false,
      ['decrypt'],
    );
  
    const plainBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      ctWithTag
    );
    return new Uint8Array(plainBuf);
  }
  
  // Attempt to parse a legacy plaintext message (if any)
  function tryDecodeLegacyMessage(data) {
    // shape: { msgId, senderId, chatMsgStr, sentStamp, chatId, isEditable? }
    if (!data) return null;
    if (typeof data.ciphertext === 'string') return null; // looks encrypted
    if (!data.msgId || !data.senderId || !data.chatMsgStr || !data.chatId) return null;
    let sent = new Date(0);
    if (data.sentStamp?.toDate) sent = data.sentStamp.toDate();
    else if (data.sentStamp instanceof Date) sent = data.sentStamp;
    return {
      msgId: data.msgId,
      senderId: data.senderId,
      chatMsgStr: data.chatMsgStr,
      sentStamp: sent,
      chatId: data.chatId,
      isEditable: !!data.isEditable,
    };
  }
  
  /**
   * Fetch and decode messages for a chat.
   * ownerUid: the data owner whose uKEK can unwrap the message DEKs (usually seed.llfcid)
   * chatId:   chats/{chatId}/messages
   */
  export async function fetchAndDecodeChat(ownerUid, chatId, { max = 200 } = {}) {
    const db = getFirestore();
    const q = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('sentStamp', 'asc'),
      fslimit(max)
    );
    const snap = await getDocs(q);
    if (snap.empty) return [];
  
    // Decode in parallel
    const results = await Promise.all(snap.docs.map(async (docSnap) => {
      const m = docSnap.data();
  
      // Legacy fast-path
      const legacy = tryDecodeLegacyMessage(m);
      if (legacy) return legacy;
  
      // Encrypted path
      const wrappedDEK = m.wrappedDEK;
      const nonceB64   = m.nonce;
      const ctB64      = m.ciphertext;
      const tagB64     = m.authTag;
  
      if (!wrappedDEK || !nonceB64 || !ctB64 || !tagB64) return null;
  
      try {
        const dekB64 = await unwrapDek(ownerUid, wrappedDEK);
        const plainBytes = await aesGcmDecrypt({
          dekB64, nonceB64, ctB64, tagB64,
        });
        const plainText = new TextDecoder().decode(plainBytes);
        const secret = JSON.parse(plainText); // ChatMsgSecret
  
        return {
          msgId: secret.msgId,
          senderId: secret.senderId,
          chatMsgStr: secret.chatMsgStr,
          isEditable: !!secret.isEditable,
          sentStamp: new Date(secret.sentStamp), // if your Swift encoded ISO8601; adjust if needed
          chatId: secret.chatId,
        };
      } catch (e) {
        console.error('decrypt message failed', docSnap.id, e);
        return null; // skip bad docs rather than failing whole fetch
      }
    }));
  
    // Filter any nulls
    return results.filter(Boolean);
  }
  